import {
  Bell,
  BookOpen,
  Bot,
  Brain,
  Calculator,
  Calendar,
  CheckCircle2,
  Code,
  Eye,
  FileText,
  GraduationCap,
  History,
  Mail,
  MousePointerClick,
  Search,
  Sparkles,
  Users,
  Wrench,
  Zap,
  type LucideIcon,
} from "lucide-react";

import { cn } from "./utils";

/**
 * Core tool-icon system. One place decides how a tool looks everywhere it is
 * shown (cards, pickers, sheets, the landing page): a rounded gradient tile
 * with a distinctive glyph and a colour per tool, the same treatment
 * Extensions get from their logo tile. The tool's name is always rendered by
 * the caller, so the glyph is decorative (`aria-hidden`).
 */

export type ToolCatalogTone =
  | "sky"
  | "violet"
  | "emerald"
  | "amber"
  | "fuchsia"
  | "rose"
  | "cyan"
  | "indigo";

export type ToolCatalogVisual = {
  icon: LucideIcon;
  tone: ToolCatalogTone;
  tagline?: string;
};

export type ToolCatalogToneStyle = {
  iconGradient: string;
  iconShadow: string;
  cardBorder: string;
  cardSelected: string;
  cardHover: string;
  check: string;
};

export const TOOL_CATALOG_TONE_STYLES: Record<
  ToolCatalogTone,
  ToolCatalogToneStyle
> = {
  sky: {
    iconGradient: "from-sky-500 to-blue-600",
    iconShadow: "shadow-sky-500/25",
    cardBorder: "border-sky-500/20",
    cardSelected: "border-sky-500/50 bg-sky-500/[0.08] ring-1 ring-sky-500/25",
    cardHover: "hover:border-sky-500/35 hover:bg-sky-500/[0.04]",
    check: "text-sky-600 dark:text-sky-400",
  },
  violet: {
    iconGradient: "from-violet-500 to-purple-600",
    iconShadow: "shadow-violet-500/25",
    cardBorder: "border-violet-500/20",
    cardSelected:
      "border-violet-500/50 bg-violet-500/[0.08] ring-1 ring-violet-500/25",
    cardHover: "hover:border-violet-500/35 hover:bg-violet-500/[0.04]",
    check: "text-violet-600 dark:text-violet-400",
  },
  emerald: {
    iconGradient: "from-emerald-500 to-teal-600",
    iconShadow: "shadow-emerald-500/25",
    cardBorder: "border-emerald-500/20",
    cardSelected:
      "border-emerald-500/50 bg-emerald-500/[0.08] ring-1 ring-emerald-500/25",
    cardHover: "hover:border-emerald-500/35 hover:bg-emerald-500/[0.04]",
    check: "text-emerald-600 dark:text-emerald-400",
  },
  amber: {
    iconGradient: "from-amber-500 to-orange-600",
    iconShadow: "shadow-amber-500/25",
    cardBorder: "border-amber-500/20",
    cardSelected:
      "border-amber-500/50 bg-amber-500/[0.08] ring-1 ring-amber-500/25",
    cardHover: "hover:border-amber-500/35 hover:bg-amber-500/[0.04]",
    check: "text-amber-600 dark:text-amber-400",
  },
  fuchsia: {
    iconGradient: "from-fuchsia-500 to-pink-600",
    iconShadow: "shadow-fuchsia-500/25",
    cardBorder: "border-fuchsia-500/20",
    cardSelected:
      "border-fuchsia-500/50 bg-fuchsia-500/[0.08] ring-1 ring-fuchsia-500/25",
    cardHover: "hover:border-fuchsia-500/35 hover:bg-fuchsia-500/[0.04]",
    check: "text-fuchsia-600 dark:text-fuchsia-400",
  },
  rose: {
    iconGradient: "from-rose-500 to-red-600",
    iconShadow: "shadow-rose-500/25",
    cardBorder: "border-rose-500/20",
    cardSelected:
      "border-rose-500/50 bg-rose-500/[0.08] ring-1 ring-rose-500/25",
    cardHover: "hover:border-rose-500/35 hover:bg-rose-500/[0.04]",
    check: "text-rose-600 dark:text-rose-400",
  },
  cyan: {
    iconGradient: "from-cyan-500 to-teal-600",
    iconShadow: "shadow-cyan-500/25",
    cardBorder: "border-cyan-500/20",
    cardSelected:
      "border-cyan-500/50 bg-cyan-500/[0.08] ring-1 ring-cyan-500/25",
    cardHover: "hover:border-cyan-500/35 hover:bg-cyan-500/[0.04]",
    check: "text-cyan-600 dark:text-cyan-400",
  },
  indigo: {
    iconGradient: "from-indigo-500 to-violet-600",
    iconShadow: "shadow-indigo-500/25",
    cardBorder: "border-indigo-500/20",
    cardSelected:
      "border-indigo-500/50 bg-indigo-500/[0.08] ring-1 ring-indigo-500/25",
    cardHover: "hover:border-indigo-500/35 hover:bg-indigo-500/[0.04]",
    check: "text-indigo-600 dark:text-indigo-400",
  },
};

/** The 15 built-in catalog tools (python/app/seed.py DEFAULT_TOOL_NAMES), in display order. */
export const CATALOG_TOOL_NAMES = [
  "Step-by-Step Reasoning",
  "RAG",
  "Direct Answer",
  "Web Search",
  "Browser Automation",
  "Python Interpreter",
  "Long Memory",
  "Human Escalation",
  "Notify",
  "Request Approval",
  "Recall Interactions",
  "Spawn Sub-Agent",
  "Calculator",
  "Vision Extract",
  "Self-Learning",
] as const;

const TOOL_VISUAL_BY_NAME: Record<string, ToolCatalogVisual> = {
  "browser automation": {
    icon: MousePointerClick,
    tone: "cyan",
    tagline: "Navigate pages and run actions in a browser",
  },
  calculator: {
    icon: Calculator,
    tone: "amber",
    tagline: "Evaluate math expressions accurately",
  },
  "direct answer": {
    icon: Zap,
    tone: "emerald",
    tagline: "Reply without calling external tools",
  },
  "human escalation": {
    icon: Users,
    tone: "rose",
    tagline: "Hand off to a person when needed",
  },
  "long memory": {
    icon: Brain,
    tone: "violet",
    tagline: "Persist facts across conversations",
  },
  notify: {
    icon: Bell,
    tone: "amber",
    tagline: "Send alerts and notifications",
  },
  "python interpreter": {
    icon: Code,
    tone: "indigo",
    tagline: "Run Python for data and logic tasks",
  },
  rag: {
    icon: BookOpen,
    tone: "emerald",
    tagline: "Search your knowledge base for answers",
  },
  "request approval": {
    icon: CheckCircle2,
    tone: "rose",
    tagline: "Pause for human approval before acting",
  },
  "spawn sub-agent": {
    icon: Bot,
    tone: "violet",
    tagline: "Delegate work to specialized sub-agents",
  },
  "step-by-step reasoning": {
    icon: Sparkles,
    tone: "fuchsia",
    tagline: "Show structured reasoning before answering",
  },
  "web search": {
    icon: Search,
    tone: "sky",
    tagline: "Look up live information on the web",
  },
  "recall interactions": {
    icon: History,
    tone: "fuchsia",
    tagline: "Recall prior customer interactions",
  },
  "vision extract": {
    icon: Eye,
    tone: "sky",
    tagline: "Read text and details from images",
  },
  "self-learning": {
    icon: GraduationCap,
    tone: "emerald",
    tagline: "Learn durable facts from its own grounded answers",
  },
};

/** Neutral glyph for a tool the system has no visual for. */
export const UNKNOWN_TOOL_VISUAL: ToolCatalogVisual = {
  icon: Wrench,
  tone: "sky",
};

/** Glyphs for non-catalog tools reached through the fuzzy fallbacks below. */
const EMAIL_VISUAL: ToolCatalogVisual = { icon: Mail, tone: "rose" };
const CALENDAR_VISUAL: ToolCatalogVisual = { icon: Calendar, tone: "amber" };
const FILE_VISUAL: ToolCatalogVisual = { icon: FileText, tone: "emerald" };

/** True when the name has its own entry in the catalog map (not a fuzzy or fallback match). */
export function hasExplicitToolVisual(toolName: string): boolean {
  return Object.hasOwn(TOOL_VISUAL_BY_NAME, toolName.trim().toLowerCase());
}

export function getToolVisual(toolName: string): ToolCatalogVisual {
  const key = toolName.trim().toLowerCase();
  if (!key) return UNKNOWN_TOOL_VISUAL;

  const exact = Object.hasOwn(TOOL_VISUAL_BY_NAME, key)
    ? TOOL_VISUAL_BY_NAME[key]
    : undefined;
  if (exact) return exact;

  if (key.includes("browser")) {
    return TOOL_VISUAL_BY_NAME["browser automation"]!;
  }
  if (key.includes("memory")) {
    return TOOL_VISUAL_BY_NAME["long memory"]!;
  }
  if (key.includes("search")) {
    return TOOL_VISUAL_BY_NAME["web search"]!;
  }
  if (key.includes("python") || key.includes("code")) {
    return TOOL_VISUAL_BY_NAME["python interpreter"]!;
  }
  if (key.includes("reason")) {
    return TOOL_VISUAL_BY_NAME["step-by-step reasoning"]!;
  }
  if (key.includes("escalat")) {
    return TOOL_VISUAL_BY_NAME["human escalation"]!;
  }
  if (key.includes("approv")) {
    return TOOL_VISUAL_BY_NAME["request approval"]!;
  }
  if (key.includes("sub-agent") || key.includes("sub agent")) {
    return TOOL_VISUAL_BY_NAME["spawn sub-agent"]!;
  }
  if (key.includes("recall")) {
    return TOOL_VISUAL_BY_NAME["recall interactions"]!;
  }
  if (key.includes("vision")) {
    return TOOL_VISUAL_BY_NAME["vision extract"]!;
  }
  if (key.includes("self-learning") || key.includes("self learning")) {
    return TOOL_VISUAL_BY_NAME["self-learning"]!;
  }
  if (key.includes("calc")) {
    return TOOL_VISUAL_BY_NAME["calculator"]!;
  }
  if (key.includes("email") || key.includes("mail")) return EMAIL_VISUAL;
  if (key.includes("calendar") || key.includes("booking")) {
    return CALENDAR_VISUAL;
  }
  if (key.includes("file") || key.includes("doc")) return FILE_VISUAL;

  return UNKNOWN_TOOL_VISUAL;
}

export type ToolIconSize = "xs" | "sm" | "md" | "lg";

/** Tile and glyph classes per size: 20, 24, 32 and 40 px tiles. */
export const TOOL_ICON_SIZES: Record<
  ToolIconSize,
  { tile: string; glyph: string; shadow: string }
> = {
  xs: { tile: "size-5 rounded-md", glyph: "size-3", shadow: "shadow-xs" },
  sm: { tile: "size-6 rounded-md", glyph: "size-3.5", shadow: "shadow-xs" },
  md: { tile: "size-8 rounded-lg", glyph: "size-4", shadow: "shadow-sm" },
  lg: {
    tile: "size-10 rounded-xl",
    glyph: "size-[1.125rem]",
    shadow: "shadow-md",
  },
};

export function ToolIconTile({
  icon: Icon,
  tone,
  size = "md",
  className,
  title,
}: {
  icon: LucideIcon;
  tone: ToolCatalogTone;
  size?: ToolIconSize;
  className?: string;
  title?: string;
}) {
  const sizing = TOOL_ICON_SIZES[size];
  const toneStyle = TOOL_CATALOG_TONE_STYLES[tone];
  return (
    <span
      data-slot="tool-icon"
      title={title}
      className={cn(
        "inline-flex shrink-0 items-center justify-center bg-gradient-to-br text-white",
        sizing.tile,
        sizing.shadow,
        toneStyle.iconGradient,
        toneStyle.iconShadow,
        className,
      )}
    >
      <Icon className={sizing.glyph} aria-hidden />
    </span>
  );
}

/**
 * A tool's icon tile. Unknown names get a neutral wrench. The tool's name is
 * always shown by the caller, so the glyph itself is hidden from assistive tech.
 */
export function ToolIcon({
  toolName,
  size = "md",
  className,
  title,
}: {
  toolName: string;
  size?: ToolIconSize;
  className?: string;
  title?: string;
}) {
  const visual = getToolVisual(toolName);
  return (
    <ToolIconTile
      icon={visual.icon}
      tone={visual.tone}
      size={size}
      className={className}
      title={title}
    />
  );
}
